import { BarChart3, ClipboardList, LayoutDashboard, Menu, Users } from "lucide-react";
import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { useIsAdmin, useIsMember } from "../lib/permissions";
import { useNavBadges } from "./app-sidebar";
import { ALERT_BADGES, type BadgeKey, ISSUES_NAV as ISSUES, type NavItem, NOTIFICATIONS } from "./nav";
import { useSidebar } from "./ui/sidebar";

/** The four destinations each role reaches most; everything else is one tap away under More. */
const ADMIN_TABS: NavItem[] = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true, badge: "attention" },
  ISSUES,
  { to: "/tasks", label: "Work orders", icon: ClipboardList },
  { ...NOTIFICATIONS, label: "Inbox" },
];
const USER_TABS: NavItem[] = [
  ISSUES,
  { to: "/teams", label: "Teams", icon: Users },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { ...NOTIFICATIONS, label: "Inbox" },
];
const VIEWER_TABS: NavItem[] = [ISSUES, { ...NOTIFICATIONS, label: "Inbox" }];

const isActive = (item: NavItem, pathname: string) =>
  item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);

/**
 * The phone's primary navigation, pinned above the home indicator. It replaces the sidebar
 * trigger below `md`; the full sidebar still opens as a sheet from More.
 */
export function MobileTabBar() {
  const { pathname } = useLocation();
  const { openMobile, setOpenMobile } = useSidebar();
  const admin = useIsAdmin();
  const member = useIsMember();
  const badges = useNavBadges();
  const tabs = admin ? ADMIN_TABS : member ? USER_TABS : VIEWER_TABS;
  const moreActive = openMobile || !tabs.some((tab) => isActive(tab, pathname));

  return (
    <nav
      aria-label="Primary"
      className="tab-bar fixed inset-x-0 bottom-0 z-30 border-t bg-background/85 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl backdrop-saturate-150 select-none md:hidden"
    >
      <ul className="mx-auto flex h-14 max-w-lg items-stretch px-[max(0.5rem,env(safe-area-inset-left))]">
        {tabs.map((tab) => {
          const active = !openMobile && isActive(tab, pathname);
          const count = tab.badge ? badges[tab.badge] : 0;
          return (
            <li key={tab.to} className="flex flex-1">
              <NavLink
                to={tab.to}
                end={tab.end}
                aria-current={active ? "page" : undefined}
                aria-label={count > 0 ? `${tab.label}, ${count}` : tab.label}
                onClick={() => {
                  setOpenMobile(false);
                  if (active) window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="flex flex-1"
              >
                <TabFace
                  icon={<tab.icon />}
                  label={tab.label}
                  active={active}
                  count={count}
                  tone={tab.badge}
                />
              </NavLink>
            </li>
          );
        })}
        <li className="flex flex-1">
          <button
            type="button"
            aria-expanded={openMobile}
            onClick={() => setOpenMobile(!openMobile)}
            className="flex flex-1"
          >
            <TabFace icon={<Menu />} label="More" active={moreActive} />
          </button>
        </li>
      </ul>
    </nav>
  );
}

function TabFace({
  icon,
  label,
  active,
  count = 0,
  tone,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  count?: number;
  tone?: BadgeKey;
}) {
  return (
    <span
      className={cn(
        "flex flex-1 flex-col items-center justify-center gap-1 text-[10.5px] leading-none font-medium tracking-tight transition-[color,transform] duration-(--motion-dur) active:scale-92",
        active ? "text-foreground" : "text-muted-foreground",
      )}
    >
      <span
        className={cn(
          "relative flex h-7 w-12 items-center justify-center rounded-full transition-colors duration-(--motion-dur) [&_svg]:size-5",
          active && "bg-primary/10 text-primary dark:bg-primary/15",
        )}
      >
        {icon}
        {count > 0 && (
          <span
            className={cn(
              "absolute -top-0.5 right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums ring-2 ring-background",
              tone && ALERT_BADGES.has(tone) ? "bg-amber-500 text-white" : "bg-muted text-foreground",
            )}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </span>
      {label}
    </span>
  );
}
